import { AnnouncementsContext, useAnnouncement } from '@tour-kit/announcements';
import { Badge, Button, Card, CardContent, cn } from '@up2stay/up2promo-ui';
import { Megaphone } from 'lucide-react';
import { useContext } from 'react';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { ListEmptyState } from '@/core/components/layout';
import { PageHeader } from '@/core/components/page-header';
import {
  SETTINGS_SECTION_DESCRIPTION_CLASS,
  SETTINGS_SECTION_TITLE_CLASS,
} from '@/core/components/settings/settings-section';
import {
  releaseAnnouncements,
  announcementCopyFor,
} from '@/core/guidance/announcements-registry';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { shellUsesMobileTabBar } from '@/core/utils/shell-chrome';
import { AppIonPage } from '@/core/components/app-ion-page';

function WhatsNewBreadcrumb({
  domainLabel,
  whatsNewLabel,
}: {
  domainLabel: string;
  whatsNewLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {whatsNewLabel}
    </>
  );
}

function WhatsNewEntryRow({ id }: { id: string }) {
  const dict = useDictionary();
  const a = dict.guidance.announcements;
  const entryCopy = announcementCopyFor(dict, id);
  const { forceShow, isDismissed } = useAnnouncement(id);
  const entry = releaseAnnouncements().find((item) => item.id === id);

  if (!entryCopy || !entry) return null;

  const dateLabel = entry.publishedAt
    ? new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(new Date(entry.publishedAt))
    : null;

  return (
    <li>
      <Card className="min-w-0 overflow-hidden">
        <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between sm:p-6">
          <div className="min-w-0 flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={SETTINGS_SECTION_TITLE_CLASS}>
                {entryCopy.title}
              </h2>
              {isDismissed ? (
                <Badge variant="secondary">{a.seenBadge}</Badge>
              ) : null}
            </div>
            {dateLabel ? (
              <p className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                {dateLabel}
              </p>
            ) : null}
            <p className={SETTINGS_SECTION_DESCRIPTION_CLASS}>
              {entryCopy.body}
            </p>
          </div>
          <Button
            type="button"
            variant="ghostAccent"
            size="sm"
            className="shrink-0 self-start"
            onClick={() => forceShow()}
          >
            {a.replay}
          </Button>
        </CardContent>
      </Card>
    </li>
  );
}

function WhatsNewReleaseList() {
  const announcementsReady = useContext(AnnouncementsContext) != null;
  const dict = useDictionary();
  const a = dict.guidance.announcements;
  const releases = releaseAnnouncements();

  if (!announcementsReady) {
    return null;
  }

  if (releases.length === 0) {
    return (
      <ListEmptyState
        icon={<Megaphone className="h-6 w-6" aria-hidden />}
        title={a.empty}
      />
    );
  }

  return (
    <ul className={cn('flex flex-col gap-4')}>
      {releases.map((entry) => (
        <WhatsNewEntryRow key={entry.id} id={entry.id} />
      ))}
    </ul>
  );
}

export function WhatsNewPage() {
  const dict = useDictionary();
  const a = dict.guidance.announcements;
  const backHref = shellUsesMobileTabBar()
    ? shellPaths.moreHub
    : shellPaths.home;

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout>
          <PageHeader
            breadcrumb={
              <WhatsNewBreadcrumb
                domainLabel={dict.nav.account}
                whatsNewLabel={a.whatsNew}
              />
            }
            title={a.whatsNewTitle}
            backHref={backHref}
          />
          <div className="mx-auto w-full max-w-2xl">
            <WhatsNewReleaseList />
          </div>
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
